import { FC, useState } from "react";
import countries from "world-countries";
import { CloseIcon, PenIcon } from "../../../assets/icons";
import {
  LoanProductType,
  MediaPurpose,
  Vendor,
  VendorMediaPurpose,
} from "../../../hooks/api/types";
import TradeLicenseModal from "./TradeLicenseModal";
import DeleteVendorModal from "./DeleteVendorModal";

type Props = {
  vendor: Vendor;
  onDelete: () => void;
  onEdit: () => void;
  className?: string;
  loanApplicationId?: string;
  onVendorUpdate?: () => void;
  loanType?: LoanProductType;
};

const VendorCard: FC<Props> = ({
  vendor,
  onDelete,
  onEdit,
  className,
  loanApplicationId,
  onVendorUpdate,
  loanType,
}) => {
  const [showTradeLicenseModal, setShowTradeLicenseModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDeleteConfirm = () => {
    onDelete();
    setShowDeleteModal(false);
  };

  const handleDocumentDelete = () => {
    if (onVendorUpdate) {
      onVendorUpdate();
    }
  };

  return (
    <>
      <div
        key={vendor.vendorId}
        className={`cx-flex cx-flex-row cx-items-center cx-justify-between cx-w-96 cx-max-h-[85px] cx-p-4 cx-border cx-border-stroke-primary cx-rounded-base cx-mb-4 ${className}`}
      >
        <div
          className={"cx-flex cx-flex-row cx-items-center cx-overflow-hidden"}
        >
          <div className={"cx-font-bold cx-text-2xl cx-flex-shrink-0"}>
            {countries.find((country) => country.cca3 === vendor.country)?.flag}
          </div>
          <div
            className={
              "cx-flex cx-flex-col cx-ml-4 cx-justify-around cx-overflow-hidden"
            }
          >
            <div className={"cx-font-medium cx-text-base cx-truncate"}>
              {vendor.name}
            </div>
            {vendor.licenseNumber && (
              <div
                className={
                  "cx-text-s cx-font-medium cx-text-text-secondary cx-truncate"
                }
              >
                {vendor.licenseNumber}
              </div>
            )}
            {vendor?.documents?.length && vendor?.documents?.length > 0 ? (
              <div
                className={
                  "cx-text-xs cx-font-medium cx-text-text-brand cx-cursor-pointer hover:cx-underline"
                }
                onClick={() => setShowTradeLicenseModal(true)}
              >
                View trade license
              </div>
            ) : null}
          </div>
        </div>
        <div className={"cx-flex cx-flex-row cx-gap-3"}>
          <div
            className={
              "cx-group cx-w-8 cx-h-8 cx-flex cx-justify-center cx-items-center cx-rounded-full cx-border cx-border-stroke-primary cx-cursor-pointer hover:cx-bg-background-brand"
            }
            onClick={onEdit}
          >
            <PenIcon className="cx-text-text-brand group-hover:cx-text-text-inverse" />
          </div>
          <div
            className={
              "cx-group cx-w-8 cx-h-8 cx-flex cx-justify-center cx-items-center cx-rounded-full cx-border cx-border-stroke-error cx-cursor-pointer hover:cx-bg-background-error"
            }
            onClick={() => setShowDeleteModal(true)}
          >
            <CloseIcon className="cx-text-text-error group-hover:cx-text-text-inverse" />
          </div>
        </div>
      </div>

      <TradeLicenseModal
        isOpen={showTradeLicenseModal}
        onClose={() => setShowTradeLicenseModal(false)}
        documentIds={
          vendor.documents
            ?.filter(
              (doc) => doc.documentPurpose === MediaPurpose.VENDOR_TRADE_LICENSE
            )
            ?.map((doc) => doc.documentId) || []
        }
        vendor={vendor}
        loanApplicationId={loanApplicationId}
        onDocumentDelete={handleDocumentDelete}
      />

      <DeleteVendorModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        vendor={vendor}
        loanApplicationId={loanApplicationId}
        onVendorUpdate={onVendorUpdate}
        loanType={loanType}
      />
    </>
  );
};

export default VendorCard;
